import { useState } from 'react';
import { cn } from '@localagent/ui/lib/utils';
import { CopyButton } from '../CopyButton';
import { resolveServiceName } from '../errors/error-messages';
import {
  formatErrorForCopy,
  formatErrorReport,
  formatDetails,
} from './notification-formatters';
import type { ErrorEntry } from '../../types/error-events-types';

const SEVERITY_DOT: Record<ErrorEntry['severity'], string> = {
  warning: 'bg-yellow-500',
  error: 'bg-red-500',
  critical: 'bg-red-500 animate-pulse',
};

export interface ErrorDetailsExpanderProps {
  entry: ErrorEntry;
  /** Present → renders a session-link affordance (only when the entry carries a
   *  `session_id`). The parent navigates to `/chat/:sessionId`. */
  onNavigateSession?: (sessionId: string) => void;
  /** Toggle this row's read state (per-org for errors). */
  onToggleRead?: (entry: ErrorEntry) => void;
}

/**
 * One error row with a details expander + a report affordance.
 *
 * Presentation only — all state (expanded, report-open) is local; persistence
 * (read/dismiss) and navigation are delegated to the parent via callbacks. The
 * "Report" affordance reveals a copy-ready structured bug report INCLUDING the
 * `details`/stack.
 */
export function ErrorDetailsExpander({
  entry,
  onNavigateSession,
  onToggleRead,
}: ErrorDetailsExpanderProps) {
  const [expanded, setExpanded] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const detailsText = formatDetails(entry.details);

  return (
    <div
      data-testid="error-entry"
      data-error-code={entry.error_code}
      className={cn('px-6 py-4', !entry.read && 'bg-accent/30')}
    >
      <div className="flex items-center gap-2">
        {!entry.read && (
          <span
            className="inline-block h-1.5 w-1.5 flex-shrink-0 rounded-full bg-blue-500"
            title="Unread"
          />
        )}
        <span
          className={cn(
            'inline-block h-2 w-2 flex-shrink-0 rounded-full',
            SEVERITY_DOT[entry.severity],
          )}
          data-testid="error-severity-dot"
          data-severity={entry.severity}
        />
        <span className="text-xs font-medium text-muted-foreground">
          {resolveServiceName(entry.service_origin)}
        </span>
        <span className="font-mono text-xs text-muted-foreground/70">{entry.error_code}</span>
        <span className="ml-auto text-xs text-muted-foreground">
          {new Date(entry.timestamp).toLocaleString()}
        </span>
      </div>

      <p className="mt-1 whitespace-pre-wrap break-words text-sm">{entry.message}</p>

      <div className="mt-2 flex flex-wrap items-center gap-3">
        {entry.occurrenceCount > 1 && (
          <span className="text-xs text-muted-foreground">
            {entry.occurrenceCount} occurrences
          </span>
        )}

        <div className="ml-auto flex items-center gap-2">
          {detailsText && (
            <button
              type="button"
              data-testid="error-details-toggle"
              aria-expanded={expanded}
              onClick={() => setExpanded((v) => !v)}
              className="rounded bg-muted px-1.5 py-0.5 text-xs text-muted-foreground transition-colors hover:bg-accent/50"
            >
              {expanded ? 'Hide details' : 'Details'}
            </button>
          )}

          {entry.session_id && onNavigateSession && (
            <button
              type="button"
              data-testid="error-session-link"
              onClick={() => onNavigateSession(entry.session_id!)}
              className="rounded bg-muted px-1.5 py-0.5 text-xs text-muted-foreground transition-colors hover:bg-accent/50"
            >
              Open session
            </button>
          )}

          <button
            type="button"
            data-testid="error-report-button"
            aria-pressed={reportOpen}
            onClick={() => setReportOpen((v) => !v)}
            className="rounded bg-muted px-1.5 py-0.5 text-xs text-muted-foreground transition-colors hover:bg-accent/50"
          >
            Report
          </button>

          <CopyButton text={formatErrorForCopy(entry)} />

          {onToggleRead && (
            <button
              type="button"
              data-testid="error-toggle-read"
              onClick={() => onToggleRead(entry)}
              className="rounded bg-muted px-1.5 py-0.5 text-xs text-muted-foreground transition-colors hover:bg-accent/50"
            >
              {entry.read ? 'Mark unread' : 'Mark read'}
            </button>
          )}
        </div>
      </div>

      {expanded && detailsText && (
        <pre
          data-testid="error-details-content"
          className="mt-2 max-h-64 overflow-auto rounded bg-muted/60 p-2 text-xs"
        >
          {detailsText}
        </pre>
      )}

      {reportOpen && (
        <div className="mt-2" data-testid="error-report-content">
          <p className="mb-1 text-xs text-muted-foreground">
            Copy this report when filing a bug:
          </p>
          <pre className="max-h-64 overflow-auto rounded bg-muted/60 p-2 text-xs">
            {formatErrorReport(entry)}
          </pre>
          <div className="mt-1">
            <CopyButton text={formatErrorReport(entry)} />
          </div>
        </div>
      )}
    </div>
  );
}
